import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchSavedJobs, unsaveJob } from "../lib/api";
import { useAuth } from "../lib/auth";
import { categoryFor } from "../lib/categories";
import { CategoryIcon } from "../lib/categoryIcons";
import { X, ArrowLeft, ExternalLink, Bookmark, BookmarkX, MapPin } from "lucide-react";
import GoogleButton from "./GoogleButton";

function fmt(s) {
    if (!s) return "";
    return String(s).slice(0, 10);
}

export default function SavedJobsPage({ onClose, onToast, onOpenJob }) {
    const { user, loading: authLoading, login } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const list = await fetchSavedJobs();
                setItems(list);
            } finally {
                setLoading(false);
            }
        })();
    }, [user, authLoading]);

    const handleRemove = async (jobId) => {
        try {
            await unsaveJob(jobId);
            setItems((xs) => xs.filter((x) => x.job_id !== jobId));
            onToast && onToast("Removed from saved jobs.");
        } catch {
            onToast && onToast("Couldn't remove that just now.");
        }
    };

    if (!authLoading && !user) {
        return (
            <div className="fixed inset-0 z-[600] bg-white flex items-center justify-center p-6" data-testid="saved-gate">
                <div className="max-w-md w-full text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ffdccb] text-[#e85812] flex items-center justify-center mb-4">
                        <Bookmark className="w-7 h-7" />
                    </div>
                    <h1 className="font-display font-extrabold text-2xl text-[#1a1e2c] mb-2">
                        Sign in to save jobs
                    </h1>
                    <p className="text-slate-500 font-body text-[14px] mb-5">
                        Sign in with Google to bookmark jobs and come back to apply later.
                    </p>
                    <GoogleButton onClick={login} label="Continue with Google" />
                    <button className="mt-4 text-sm text-slate-500 hover:text-[#e85812] underline" onClick={onClose}>
                        Back to jobs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[600] bg-white overflow-auto" data-testid="saved-page">
            <div className="max-w-3xl mx-auto p-5 md:p-8">
                <div className="flex items-center justify-between mb-4">
                    <button className="btn-ghost !py-2 !px-3" onClick={onClose} data-testid="saved-back">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button className="btn-ghost !py-2 !px-3" onClick={onClose} aria-label="close" data-testid="saved-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="mb-6">
                    <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#1a1e2c]">
                        Saved jobs
                    </h1>
                    <p className="text-slate-500 font-body text-sm mt-0.5">
                        {items.length} job{items.length === 1 ? "" : "s"} you bookmarked — most recent first.
                    </p>
                </div>

                {loading && <div className="text-slate-500 text-sm">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="card-soft p-8 text-center" data-testid="saved-empty">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#fff2ea] text-[#e85812] flex items-center justify-center mb-3">
                            <Bookmark className="w-7 h-7" />
                        </div>
                        <div className="font-display font-semibold text-[#1a1e2c] text-lg">
                            No saved jobs yet
                        </div>
                        <p className="text-slate-500 text-sm mt-1 max-w-xs mx-auto">
                            Tap the bookmark icon on any job to save it here for later.
                        </p>
                        <button onClick={onClose} className="btn-primary mt-5" data-testid="saved-browse">
                            Browse jobs
                        </button>
                    </div>
                )}
                <div className="flex flex-col gap-3">
                    {items.map((r) => {
                        const cat = categoryFor(r.cat || "_default");
                        return (
                            <motion.div
                                key={r.job_id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="card-soft p-4 flex items-start gap-3"
                                data-testid={`saved-row-${r.job_id}`}
                            >
                                <div
                                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: cat.color }}
                                >
                                    <CategoryIcon catId={cat.id} size={22} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold text-[15px] text-[#1a1e2c] truncate">
                                        {r.job_title}
                                    </div>
                                    <div className="text-[13px] text-slate-600 truncate">{r.company}</div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[12px] text-slate-500">
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {r.loc || r.city || "—"}
                                        </span>
                                        {r.pay && <span className="text-[#e85812] font-semibold">{r.pay}</span>}
                                        <span>Saved {fmt(r.saved_at)}</span>
                                    </div>
                                </div>
                                {onOpenJob && (
                                    <button
                                        className="btn-primary !py-2 !px-3 !text-xs shrink-0"
                                        onClick={() => onOpenJob(r)}
                                        data-testid={`saved-apply-${r.job_id}`}
                                    >
                                        Apply
                                    </button>
                                )}
                                {r.url && (
                                    <a
                                        href={r.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-ghost !py-2 !px-3 !text-xs shrink-0"
                                        data-testid={`saved-open-${r.job_id}`}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                                <button
                                    className="btn-ghost !py-2 !px-2 text-red-500 shrink-0"
                                    onClick={() => handleRemove(r.job_id)}
                                    data-testid={`saved-remove-${r.job_id}`}
                                    aria-label="remove"
                                    title="Remove from saved"
                                >
                                    <BookmarkX className="w-4 h-4" />
                                </button>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
